import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { Breadcrumbs, Typography } from "@mui/material";
import { NavigateNextOutlined } from "@mui/icons-material";
import { useSite } from "../../context/siteContext";
import { ROUTES } from "../../constants";
import { getMachinesByIds, getSingleProduct, getOrderByKey } from "../../services/breadCrumbsService";
import { useTranslation } from "react-i18next";
import { DEFAULT_LAYOUT } from "../../config";

const NAV_CONTEXT_KEY = (pathname) => `breadcrumb_ctx_${pathname}`;
const getMachineDisplayName = (machine) =>
  machine?.machine_name || machine?.machine_number || machine?.machine_id || null;
const saveNavContext = (pathname, state) => {
  try {
    const context = {
      fromMachineCard: state?.fromMachineCard ?? false,
      fromMachinePage: state?.fromMachinePage ?? false,
      fromSearchResults: state?.fromSearchResults ?? false,
      machineId: state?.machine?.machine_id || state?.selectedMachine?.machine_id || null,
      machineName: getMachineDisplayName(state?.machine || state?.selectedMachine)
    };
    sessionStorage.setItem(NAV_CONTEXT_KEY(pathname), JSON.stringify(context));
  } catch (_) {}
};

const loadNavContext = (pathname) => {
  try {
    const raw = sessionStorage.getItem(NAV_CONTEXT_KEY(pathname));
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
};

const BreadCrumbs = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const viewParam = searchParams.get("view");
  const fromMachineParam = searchParams.get("fromMachine");

  const pathnames = location.pathname.split("/").filter(Boolean);
  const initialStateRef = useRef(location.state);

  // State to hold fetched names
  const [machineName, setMachineName] = useState(null);
  const [productName, setProductName] = useState(null);
  const [orderName, setOrderName] = useState(null);
  const { selectedSite } = useSite();

  useEffect(() => {
    if (location.state) {
      initialStateRef.current = location.state;
      saveNavContext(location.pathname, location.state);
    } else {
      const cached = loadNavContext(location.pathname);
      if (cached) {
        initialStateRef.current = {
          fromMachineCard: cached.fromMachineCard,
          fromMachinePage: cached.fromMachinePage,
          fromSearchResults: cached.fromSearchResults,
          machine: cached.machineId
  ? {
      machine_id: cached.machineId,
      machine_name: cached.machineName,
      machine_number: cached.machineNumber,
    }
  : null,
        };
      }
    }
  }, [location.pathname, location.state]);

  useEffect(() => {
    const fetchData = async () => {
      setMachineName(null);
      setProductName(null);
      setOrderName(null);

      const machineFromState =
        initialStateRef.current?.machine || initialStateRef.current?.selectedMachine;

      // Machine fetching logic — /machines/:machineId
      if (pathnames[0] === "machines" && pathnames[1]) {
        if (machineFromState?.machine_id === pathnames[1]) {
          setMachineName(machineFromState.machine_name || pathnames[1]);
        } else {
          try {
            const response = await getMachinesByIds({ machines: [pathnames[1]] });
            if (response.status) setMachineName(response.data[0].machine_name);
            else setMachineName(pathnames[1]);
          } catch {
            setMachineName(pathnames[1]);
          }
        }
      }

      // Documentation new route — /documentation/:machineId
      if (pathnames[0] === "documentation" && pathnames[1]) {
        if (machineFromState?.machine_id === pathnames[1]) {
          setMachineName(machineFromState.machine_name || pathnames[1]);
        } else {
          try {
            const response = await getMachinesByIds({ machines: [pathnames[1]] });
            if (response.status) setMachineName(response.data[0].machine_name);
            else setMachineName(pathnames[1]);
          } catch {
            setMachineName(pathnames[1]);
          }
        }
      }

      // Legacy documentation — /documents/documentation/:machineId
      if (pathnames[0] === "documents" && pathnames[1] === "documentation" && pathnames[2]) {
        if (machineFromState?.machine_id === pathnames[2]) {
          setMachineName(machineFromState.machine_name || pathnames[2]);
        } else {
          try {
            const response = await getMachinesByIds({ machines: [pathnames[2]] });
            if (response.status) setMachineName(response.data[0].machine_name);
            else setMachineName(pathnames[2]);
          } catch {
            setMachineName(pathnames[2]);
          }
        }
      }

      // Product — /machines/:machineId/:productId
      if (pathnames[0] === "machines" && pathnames[1] && pathnames[2]) {
        try {
          const response = await getSingleProduct({
            machine_id: pathnames[1],
            product_id: pathnames[2],
            site_number: selectedSite,
          });
          if (response.status) setProductName(response.data.data[0].product_name);
          else setProductName(pathnames[2]);
        } catch {
          setProductName(pathnames[2]);
        }
      }

      // Order-history
      if (pathnames[0] === "order-history" && pathnames[1]) {
        try {
          if (machineFromState?.machine_id === pathnames[1]) {
            setMachineName(machineFromState.machine_name || pathnames[1]);
          } else {
            const response = await getMachinesByIds({ machines: [pathnames[1]] });
            if (response.status)setMachineName(getMachineDisplayName(response.data[0]) || pathnames[1]);
            else setMachineName(pathnames[1]);
          }

          if (pathnames[2]) {
            try {
              const response1 = await getSingleProduct({
                machine_id: pathnames[1],
                product_id: pathnames[2],
                site_number: selectedSite,
              });
              if (response1.status) setProductName(response1.data[0].product_name);
              else setProductName(pathnames[2]);
            } catch {
              setProductName(pathnames[2]);
            }
          }
        } catch (error) {
          console.error("Error fetching order-history names:", error);
          setMachineName(pathnames[1]);
          if (pathnames[2]) setProductName(pathnames[2]);
        }
      }

      // Order-received
      if (pathnames[0] === "order-received" && pathnames[1]) {
        try {
          if (!pathnames[1] || pathnames[1] === "undefined") return;
          const response = await getOrderByKey({ orderKey: pathnames[1] });
          if (response.status) setOrderName(response.data.order.order_number);
          else setOrderName(pathnames[1]);
        } catch {
          setOrderName(pathnames[1]);
        }
      }
    };

    fetchData();
  }, [pathnames.join("/"), selectedSite]);

  // Derived flags — now reload-safe because initialStateRef is restored from sessionStorage
  const fromSearchResults = initialStateRef.current?.fromSearchResults;
  const fromMachineCard =
    initialStateRef.current?.fromMachineCard || fromMachineParam === "true";
  const fromMachinePage = initialStateRef.current?.fromMachinePage;
  const machineFromState =
    initialStateRef.current?.machine || initialStateRef.current?.selectedMachine;

  const getSpecialLabel = () => {
    if (viewParam === "recent") return t("BREADCRUMBS.RECENTLY_VIEWED");
    if (viewParam === "historical") return t("BREADCRUMBS.HISTORICAL");
    return null;
  };
  const currentSpecialLabel = getSpecialLabel();

  // Documentation with machine ID
  if (pathnames[0] === "documentation" && pathnames[1] && pathnames[1] !== "historical-content") {
    return (
      <Breadcrumbs
        separator={<NavigateNextOutlined fontSize="small" />}
        aria-label="breadcrumb"
        style={{ marginTop: "10px", padding: "0 30px" }}
      >
        <Link to={ROUTES.HOME_ALIA} style={{ textDecoration: "none" }}>
          <Typography color="primary" fontSize="12px">{t("UI_TEXT.HOME")}</Typography>
        </Link>
        <Link to="/parts" style={{ textDecoration: "none" }}>
          <Typography color="primary" fontSize="12px">{t("BREADCRUMBS.PARTS")}</Typography>
        </Link>
        <Link to="/machines" style={{ textDecoration: "none" }}>
          <Typography color="primary" fontSize="12px">{t("BREADCRUMBS.MACHINES")}</Typography>
        </Link>
        <Typography color="black" fontSize="12px">
          {getMachineDisplayName(machineFromState) || machineName || pathnames[1]}
        </Typography>
      </Breadcrumbs>
    );
  }

  // Historical Content page
  if (pathnames[0] === "documentation" && pathnames[1] === "historical-content") {
    return (
      <Breadcrumbs
        separator={<NavigateNextOutlined fontSize="small" />}
        aria-label="breadcrumb"
        style={{ marginTop: "10px", padding: "0 30px" }}
      >
        <Link to={ROUTES.HOME_ALIA} style={{ textDecoration: "none" }}>
          <Typography color="primary" fontSize="12px">{t("UI_TEXT.HOME")}</Typography>
        </Link>
        <Link to="/documentation" style={{ textDecoration: "none" }}>
          <Typography color="primary" fontSize="12px">{t("BREADCRUMBS.DOCUMENTATION")}</Typography>
        </Link>
        <Typography color="black" fontSize="12px">
          {t("HISTORICAL_CONTENT.TITLE") || "Historical Content"}
        </Typography>
      </Breadcrumbs>
    );
  }

  // Recently Viewed / Historical Documents
  if (pathnames[0] === "documents" && pathnames[1] === "documentation" && currentSpecialLabel) {
    return (
      <Breadcrumbs
        separator={<NavigateNextOutlined fontSize="small" />}
        aria-label="breadcrumb"
        style={{ marginTop: "10px", padding: "0 30px" }}
      >
        <Link to={ROUTES.HOME_ALIA} style={{ textDecoration: "none" }}>
          <Typography color="primary" fontSize="12px">{t("UI_TEXT.HOME")}</Typography>
        </Link>
        <Link to="/documentation" style={{ textDecoration: "none" }}>
          <Typography color="primary" fontSize="12px">{t("BREADCRUMBS.DOCUMENTATION")}</Typography>
        </Link>
        <Typography color="black" fontSize="12px">{currentSpecialLabel}</Typography>
      </Breadcrumbs>
    );
  }

  // Documentation from Search Results — with machine
  if (
    pathnames[0] === "documents" &&
    pathnames[1] === "documentation" &&
    pathnames[2] &&
    fromSearchResults &&
    machineFromState
  ) {
    return (
      <Breadcrumbs
        separator={<NavigateNextOutlined fontSize="small" />}
        aria-label="breadcrumb"
        style={{ marginTop: "10px", padding: "0 30px" }}
      >
        <Link to={ROUTES.HOME_ALIA} style={{ textDecoration: "none" }}>
          <Typography color="primary" fontSize="12px">{t("UI_TEXT.HOME")}</Typography>
        </Link>
        <Link to="/search-results" style={{ textDecoration: "none" }}>
          <Typography color="primary" fontSize="12px">{t("BREADCRUMBS.SEARCH_RESULTS")}</Typography>
        </Link>
        <Typography color="black" fontSize="12px">
          {getMachineDisplayName(machineFromState) || machineName || pathnames[2]}
        </Typography>
      </Breadcrumbs>
    );
  }

  // Documentation from Search Results — category only
  if (
    pathnames[0] === "documents" &&
    pathnames[1] === "documentation" &&
    !pathnames[2] &&
    fromSearchResults
  ) {
    return (
      <Breadcrumbs
        separator={<NavigateNextOutlined fontSize="small" />}
        aria-label="breadcrumb"
        style={{ marginTop: "10px", padding: "0 30px" }}
      >
        <Link to={ROUTES.HOME_ALIA} style={{ textDecoration: "none" }}>
          <Typography color="primary" fontSize="12px">{t("UI_TEXT.HOME")}</Typography>
        </Link>
        <Link to="/search-results" style={{ textDecoration: "none" }}>
          <Typography color="primary" fontSize="12px">{t("BREADCRUMBS.SEARCH_RESULTS")}</Typography>
        </Link>
        <Typography color="black" fontSize="12px">{t("BREADCRUMBS.DOCUMENTATION")}</Typography>
      </Breadcrumbs>
    );
  }

  // Documentation with machine (Machines page source) — legacy /documents path
  if (
    pathnames[0] === "documents" &&
    pathnames[1] === "documentation" &&
    pathnames[2] &&
    !fromSearchResults
  ) {
    return (
      <Breadcrumbs
        separator={<NavigateNextOutlined fontSize="small" />}
        aria-label="breadcrumb"
        style={{ marginTop: "10px", padding: "0 30px" }}
      >
        <Link to={ROUTES.HOME_ALIA} style={{ textDecoration: "none" }}>
          <Typography color="primary" fontSize="12px">{t("UI_TEXT.HOME")}</Typography>
        </Link>
        <Link to="/parts" style={{ textDecoration: "none" }}>
          <Typography color="primary" fontSize="12px">{t("BREADCRUMBS.PARTS")}</Typography>
        </Link>
        <Link to="/machines" style={{ textDecoration: "none" }}>
          <Typography color="primary" fontSize="12px">{t("BREADCRUMBS.MACHINES")}</Typography>
        </Link>
        <Typography color="black" fontSize="12px">{machineName || pathnames[2]}</Typography>
      </Breadcrumbs>
    );
  }

  // Machines route
  if (pathnames[0] === "machines") {
    const crumbs = [
      <Link key="home" to={ROUTES.HOME_ALIA} style={{ textDecoration: "none" }}>
        <Typography color="primary" fontSize="12px">{t("UI_TEXT.HOME")}</Typography>
      </Link>,
      <Link key="parts" to="/parts" style={{ textDecoration: "none" }}>
        <Typography color="primary" fontSize="12px">{t("BREADCRUMBS.PARTS")}</Typography>
      </Link>,
    ];

    if (pathnames.length > 1) {
      crumbs.push(
        <Link key="machines" to="/machines" style={{ textDecoration: "none" }}>
          <Typography color="primary" fontSize="12px">{t("BREADCRUMBS.MACHINES")}</Typography>
        </Link>
      );
    } else {
      crumbs.push(
        <Typography key="machines" color="black" fontSize="12px">
          {t("BREADCRUMBS.MACHINES")}
        </Typography>
      );
    }

    if (pathnames[1] && machineName) {
      if (pathnames.length > 2) {
        crumbs.push(
          <Link key="machine" to={`/machines/${pathnames[1]}`} style={{ textDecoration: "none" }}>
            <Typography color="primary" fontSize="12px">{machineName}</Typography>
          </Link>
        );
      } else {
        crumbs.push(
          <Typography key="machine" color="black" fontSize="12px">{machineName}</Typography>
        );
      }
    }

    if (pathnames[2] && productName) {
      crumbs.push(
        <Typography key="product" color="black" fontSize="12px">{productName}</Typography>
      );
    }

    return (
      <Breadcrumbs
        separator={<NavigateNextOutlined fontSize="small" />}
        aria-label="breadcrumb"
        style={{ marginTop: "10px", padding: "0 30px" }}
      >
        {crumbs}
      </Breadcrumbs>
    );
  }

function toCamelCase(str) {
    return str
      .split("_")
      .map(
        (word, index) =>
          word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      )
      .join(" ");
  }
  // General Breadcrumb Item Mapping
 const breadcrumbItems = pathnames.filter((value) => value.toLowerCase() !== "home").map((value, index) => {
    const isOrderRoute = pathnames[0] === "order-received";
    const to = (() => {
      if (isOrderRoute && pathnames.length > 1) {
        const upto = Math.min(index + 2, pathnames.length);
        return `/${pathnames.slice(0, upto).join("/")}`;
      }
      return `/${pathnames.slice(0, index + 1).join("/")}`;
    })();
    let displayValue = value;

    // Determine which value to display based on the index and API results
    if (pathnames[0] === "machines" && index === 1 && machineName) {
      displayValue = machineName;
    } else if (pathnames[0] === "order-history" && index === 1 && machineName) {
      displayValue = machineName;
    } else if (pathnames[0] === "documents" && index === 1 && machineName) {
      displayValue = machineName;
    } else if (pathnames[0] === "machines" && index === 2 && productName) {
      displayValue = productName;
    } else if (pathnames[0] === "order-history" && index === 2 && productName) {
      displayValue = productName;
    } else if (pathnames[0] === "order-received" && index === 1 && orderName) {
      displayValue = orderName;
    } else if (pathnames[0] === "saved_for_later") {
      const transformedPathnames = toCamelCase(pathnames[0]);
      displayValue = transformedPathnames;
    }

    if (DEFAULT_LAYOUT === "layout2") {
      if (displayValue.toLowerCase() === "cases") {
        displayValue = t("LABELS.CASES_TICKETS", { defaultValue: "Cases (Tickets)" });
      }
      if (displayValue.toLowerCase() === "workorders") {
        displayValue = t("LABELS.WORK_ORDERS_REPORTS", { defaultValue: "Work Orders & Reports" });
      }
      if (displayValue.toLowerCase() === "recommendations") {
        displayValue = "My Lists";
      }
      if (displayValue.toLowerCase() === "order history" || displayValue.toLowerCase() === "order-history") {
        displayValue = t('BUTTON.BUY_AGAIN');
      }
      if (displayValue.toLowerCase() === "machines") {
        displayValue = "My Machines";
      }
      if (displayValue.toLowerCase() === "requests") {
        displayValue = t("LABELS.CUSTOMER_CARE_REQUESTS");
      }
    } else if (displayValue.toLowerCase() === "requests") {
      displayValue = t("LABELS.REQUEST");
    }

    // Capitalize each breadcrumb item
    // Special case for RSPL
    let capitalizedValue;
    if (displayValue.toLowerCase() === "rspl") {
      capitalizedValue = DEFAULT_LAYOUT === "layout2"
        ? t("LABELS.MY_RSPL", { defaultValue: "My RSPL" })
        : "Recommended Spare Parts Lists (RSPL)";
    } else {
      // Default capitalization for other breadcrumbs
      capitalizedValue =
        displayValue.charAt(0).toUpperCase() +
        displayValue.slice(1).replace(/-/g, " ");
    }
    return (
      <Link key={to} to={to}>
        <Typography color="black" fontSize="12px">
          {capitalizedValue}
        </Typography>
      </Link>
    );
  });

  const needsPartsPrefix = (() => {
  if (DEFAULT_LAYOUT !== "layout2") return false;
  const first = (pathnames[0] || "").toLowerCase();
  return (
    first === "search-results" ||
    first === "machines" ||
    first === "order-history" ||
    first === "buy-again" ||
    first === "rspl" ||
    first === "recommendations" ||
    first === "requests" ||
    first === "saved_for_later" ||
    first === "track-order"
  );
})();

  return (
    <Breadcrumbs
      separator={<NavigateNextOutlined fontSize="small" />}
      aria-label="breadcrumb"
      style={{ marginTop: "10px", padding: "0 30px" }}
    >
      <Link to={ROUTES.HOME_ALIA} style={{ textDecoration: "none" }}>
        <Typography color="primary" fontSize="12px">{t("UI_TEXT.HOME")}</Typography>
      </Link>
    {needsPartsPrefix && (
      <Link to="/parts">
        <Typography color="black" fontSize="12px">
          {t("BREADCRUMBS.PARTS")}
        </Typography>
      </Link>
    )}
      {breadcrumbItems}
    </Breadcrumbs>
  );
};

export default BreadCrumbs;
